import { Alert, Form, Input, InputNumber, Select, Typography } from 'antd'
import { useMerchantApplication } from '../MerchantApplicationContext'
import { OrganizationTypeLabels } from '../merchantApplicationModel'
import EducationBlock from '../components/EducationBlock'
import StepFooter from '../components/StepFooter'

const { Title, Text } = Typography

type Props = {
  onNext: () => void
  onBack: () => void
}

const organizationOptions = Object.entries(OrganizationTypeLabels).map(([value, label]) => ({
  value,
  label,
}))

const BusinessDetailsStep = ({ onNext, onBack }: Props) => {
  const { state, dispatch } = useMerchantApplication()
  const { businessDetails, hasPaymentHistory } = state
  const [form] = Form.useForm()

  const handleNext = () => {
    form.validateFields().then(() => {
      onNext()
    }).catch(() => {
      // validation errors are shown inline by Ant Design
    })
  }

  const updateField = (field: keyof typeof businessDetails, value: string) => {
    dispatch({ type: 'UPDATE_BUSINESS_DETAILS', field, value })
  }

  return (
    <>
      <div className="merchant-app-step-header">
        <Title level={3}>Business Details</Title>
        <Text type="secondary">
          A few more details about your business to get your payments account set up.
        </Text>
      </div>
      <div className="merchant-app-step-body">
        <Form
          form={form}
          layout="vertical"
          size="large"
          initialValues={businessDetails}
          onValuesChange={(changed) => {
            const [field] = Object.keys(changed)
            const value = changed[field]
            updateField(field as keyof typeof businessDetails, typeof value === 'number' ? String(value) : value ?? '')
          }}
        >
          <Form.Item
            label="Tax ID (EIN or SSN)"
            name="taxId"
            rules={[{ required: true, message: 'Tax ID is required' }]}
          >
            <Input placeholder="XX-XXXXXXX" maxLength={10} />
          </Form.Item>

          <EducationBlock title="What's my Tax ID?">
            <p style={{ margin: '0 0 8px 0' }}>
              Your Tax ID is the number the IRS uses to identify your business. For most landscaping
              businesses, this is your <strong>EIN (Employer Identification Number)</strong> — a 9-digit
              number formatted like 12-3456789.
            </p>
            <p style={{ margin: '0 0 8px 0' }}><strong>Where to find it:</strong></p>
            <ul style={{ margin: '0 0 8px 0', paddingLeft: 20 }}>
              <li>On your IRS EIN confirmation letter (CP 575)</li>
              <li>On any previously filed tax return</li>
              <li>On your W-9 form</li>
            </ul>
            <p style={{ margin: '0 0 8px 0' }}>
              If you're a sole proprietor and don't have an EIN, you can use your Social Security Number.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Don't have your EIN handy?</strong> You can look it up on the{' '}
              <a href="https://www.irs.gov/" target="_blank" rel="noopener noreferrer">IRS website</a>{' '}
              or call the IRS Business & Specialty Tax Line at 1-800-829-4933.
            </p>
          </EducationBlock>

          <Form.Item
            label="Organization Type"
            name="organizationType"
            rules={[{ required: true, message: 'Organization type is required' }]}
          >
            <Select
              placeholder="Select your business type"
              options={organizationOptions}
            />
          </Form.Item>

          <div style={{ marginTop: 32, marginBottom: 16 }}>
            <Title level={5} style={{ marginBottom: 4 }}>Payment Estimates</Title>
            {hasPaymentHistory ? (
              <Alert
                type="info"
                showIcon
                message="We estimated these based on your job history. Adjust if your upcoming season looks different."
                style={{ marginBottom: 16 }}
              />
            ) : (
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                These help our payment processor understand your expected transaction volume. It's okay to estimate — round numbers are fine.
              </Text>
            )}
          </div>

          <Form.Item
            label="Average Single Payment Amount"
            name="averagePaymentAmount"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              placeholder="e.g. 150"
            />
          </Form.Item>

          <Form.Item
            label="Maximum Single Payment Amount"
            name="maximumPaymentAmount"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              placeholder="e.g. 10,000"
            />
          </Form.Item>

          <Form.Item
            label="Total Average Monthly Payments"
            name="monthlyPaymentTotal"
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
              placeholder="e.g. 25,000"
            />
          </Form.Item>

          {!hasPaymentHistory && (
            <EducationBlock title="What should I put for payment amounts?">
              <p style={{ margin: '0 0 8px 0' }}>
                These help our payment processor understand your expected transaction volume.
                It's okay to estimate — round numbers are fine.
              </p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                  <strong>Average payment:</strong> What does a typical invoice look like?
                  For a weekly mowing visit, that might be $50–$150. For a larger install job,
                  it could be several thousand.
                </li>
                <li>
                  <strong>Maximum payment:</strong> What's the largest single invoice you'd expect
                  to send? Think about your biggest jobs. It's better to overestimate here —
                  setting this too low can cause delays when you process a large payment.
                </li>
                <li>
                  <strong>Monthly total:</strong> Roughly how much do you expect to collect through
                  SynkedUP per month across all your jobs?
                </li>
              </ul>
            </EducationBlock>
          )}
        </Form>
      </div>
      <StepFooter onBack={onBack} onNext={handleNext} />
    </>
  )
}

export default BusinessDetailsStep
